/* eslint-disable no-control-regex */
/**
 * Shared sanitization utilities for narrator content.
 * Used by: parse-narrator-segments.ts, useTTS.ts, MessageCard, and any
 * component that needs to clean AI output before display or TTS.
 *
 * Note: no-control-regex is disabled here because the regexes intentionally
 * use \x00-\x7F to match the ASCII range for non-ASCII character detection.
 */

// ── Tool name list (single source of truth for client-side) ──────────
export const TOOL_NAMES = [
  "activate_mechanic", "add_pov_character", "adjust_heat", "adjust_morale", "adjust_reputation",
  "adjust_research_points", "adjust_resource", "advance_pacing", "advance_story_arc", "advance_time",
  "apply_condition", "apply_damage", "award_xp", "blockade_route", "broadcast_world_news",
  "cancel_research", "cast_spell", "claim_location", "collect_income", "combat_action",
  "complete_quest", "complete_research", "create_faction", "create_item", "create_location",
  "create_memory", "create_npc", "create_quest", "deactivate_mechanic", "death_save",
  "defend_location", "describe_map_location", "disable_entity", "discover_route", "draw_cards",
  "draw_event_card", "encounter_roll", "end_turn", "establish_trade_route", "evolve_entity",
  "fail_quest", "flag_plot_thread", "force_advance_turn", "generate_character_memories",
  "generate_scene_image", "generate_shop", "get_travel_distance", "gm_narration_note",
  "grant_feat", "hatch_seed", "heal", "inject_encounter", "invoke_character_agent",
  "level_up", "list_pov_characters", "log_timeline_event", "long_rest", "manage_custom_rule",
  "manage_inventory", "manage_roster", "modify_entity", "move_to_region", "npc_initiative",
  "org_status", "org_upgrade", "plant_narrative_seed", "play_card", "promote_lore_to_world",
  "propose_quest", "query_map_locations", "query_nearby_locations", "queue_consequence",
  "recall_agent_notes", "record_lore_discovery", "register_kill", "register_player_commitment",
  "remove_condition", "remove_timeline_event", "research_status", "resolve_commitment",
  "resolve_encounter", "resolve_world_event", "resource_report", "rest", "reveal_entity",
  "reveal_map_location", "reveal_plot", "reveal_region", "roll_dice", "roll_encounter",
  "sabotage_location", "save_agent_note", "saving_throw", "search_lore_online", "set_music",
  "set_player_character", "set_production", "set_race", "set_scene_context", "set_turn_order",
  "set_weather", "short_rest", "skill_check", "start_research", "switch_pov", "territory_status",
  "transaction", "travel_to_location", "trigger_world_event", "update_asset_location",
  "update_custom_ai_prompts", "update_entity_relationships", "update_needs", "update_quest",
  "update_quest_objective", "update_state", "validate_action"
];

const TOOL_LINE_RE = new RegExp(
  `(?:^|\\n)\\s*(?:\`{0,3})(?:${TOOL_NAMES.join("|")})\\s*\\([\\s\\S]*?\\)\\s*(?:\`{0,3})(?:\\n|$)`,
  "gi"
);
const INLINE_TOOL_RE = new RegExp(
  `(?:\`?)(?:${TOOL_NAMES.join("|")})(?:\\([^)]*\\))?(?:\`?)`,
  "gi"
);

const LEADING_META_PATTERNS = [
  // Allow sentence terminator to be followed by whitespace, end-of-string,
  // a capital letter (model glues the next sentence straight after the period,
  // e.g. "...for the observations.You take a moment..."),
  // OR a switch to non-ASCII script (e.g. Hebrew glued straight after the leak).
  /^(?:also,\s*)?i need to\b[^.!?\n]*(?:[.!?](?=\s|$|[A-Z]|[^\x00-\x7F])|$)\s*/i,
  /^i should\b[^.!?\n]*(?:[.!?](?=\s|$|[A-Z]|[^\x00-\x7F])|$)\s*/i,
  /^i must\b[^.!?\n]*(?:[.!?](?=\s|$|[A-Z]|[^\x00-\x7F])|$)\s*/i,
  /^i will\b[^.!?\n]*(?:[.!?](?=\s|$|[A-Z]|[^\x00-\x7F])|$)\s*/i,
  /^i'll\b[^.!?\n]*(?:[.!?](?=\s|$|[A-Z]|[^\x00-\x7F])|$)\s*/i,
  /^let me\b[^.!?\n]*(?:[.!?](?=\s|$|[A-Z]|[^\x00-\x7F])|$)\s*/i,
  /^my response will\s*:?\s*/i,
  /^the response should\b[^.!?\n]*(?:[.!?](?=\s|$|[A-Z]|[^\x00-\x7F])|$)\s*/i,
  /^the narration should\b[^.!?\n]*(?:[.!?](?=\s|$|[A-Z]|[^\x00-\x7F])|$)\s*/i,
  /^the player\b[^.!?\n]*(?:[.!?](?=\s|$|[A-Z]|[^\x00-\x7F])|$)\s*/i,
  /^this (?:response|answer|scene) should\b[^.!?\n]*(?:[.!?](?=\s|$|[A-Z]|[^\x00-\x7F])|$)\s*/i,
  /^after (?:the )?(?:skill check|check|roll|dice roll)\b[^.!?\n]*(?:[.!?](?=\s|$|[A-Z]|[^\x00-\x7F])|$)\s*/i,
  /^(?:once|when) (?:the )?(?:skill check|check|roll|dice roll)\b[^.!?\n]*(?:[.!?](?=\s|$|[A-Z]|[^\x00-\x7F])|$)\s*/i,
  /^i will then\b[^.!?\n]*(?:[.!?](?=\s|$|[A-Z]|[^\x00-\x7F])|$)\s*/i,
  /^i will also\b[^.!?\n]*(?:[.!?](?=\s|$|[A-Z]|[^\x00-\x7F])|$)\s*/i,
  /^the atmosphere should\b[^.!?\n]*(?:[.!?](?=\s|$|[A-Z]|[^\x00-\x7F])|$)\s*/i,
  /^focus on\b[^.!?\n]*(?:[.!?](?=\s|$|[A-Z]|[^\x00-\x7F])|$)\s*/i,
];

const TARGET_SCRIPT_REGEX: Record<string, RegExp> = {
  he: /[\u0590-\u05FF]/,
  ru: /[\u0400-\u04FF]/,
  ar: /[\u0600-\u06FF\u0750-\u077F]/,
  ja: /[\u3040-\u30FF]/,
  ko: /[\uAC00-\uD7AF\u1100-\u11FF]/,
  zh: /[\u4E00-\u9FFF]/,
};

function stripLeadingNarratorMeta(text: string): string {
  let cleaned = text.trimStart();
  for (let i = 0; i < 8; i++) {
    const before = cleaned;
    cleaned = cleaned.replace(/^(?:my response will|response plan|plan|rewrite plan)\s*:\s*/i, "");
    cleaned = cleaned.replace(/^(?:\d+\.\s+[^\n]+(?:\n|$)){2,}/, "");
    for (const pattern of LEADING_META_PATTERNS) cleaned = cleaned.replace(pattern, "");
    if (cleaned === before) break;
  }
  return cleaned.trimStart();
}

function trimLeadingLanguageDrift(text: string, targetLang?: string): string {
  let scriptRegex = targetLang ? TARGET_SCRIPT_REGEX[targetLang] : undefined;

  if (!scriptRegex) {
    for (const re of Object.values(TARGET_SCRIPT_REGEX)) {
      if (re.test(text)) {
        scriptRegex = re;
        break;
      }
    }
  }

  if (!scriptRegex) return text;

  const firstTargetIndex = text.search(scriptRegex);
  if (firstTargetIndex <= 0) return text;

  const prefix = text.slice(0, firstTargetIndex);
  if (!/[A-Za-z]/.test(prefix)) return text;

  return text.slice(firstTargetIndex).trimStart();
}

export function stripNarratorMetaAndLanguageDrift(text: string, targetLang?: string): string {
  if (!text) return text;
  let cleaned = stripLeadingNarratorMeta(text);
  cleaned = trimLeadingLanguageDrift(cleaned, targetLang);
  cleaned = cleaned.replace(/\n{3,}/g, "\n\n").trim();
  return cleaned || text;
}

/**
 * Strip leaked AI tool-call artifacts, CHARACTER AGENT markers,
 * thinking/planning blocks, and JSON tool argument blocks.
 * Returns clean narrative prose.
 */
export function sanitizeLeakedToolCalls(text: string): string {
  if (!text) return text;
  let cleaned = text;
  cleaned = stripLeadingNarratorMeta(cleaned);
  cleaned = cleaned.replace(TOOL_LINE_RE, "\n");
  cleaned = cleaned.replace(INLINE_TOOL_RE, "");
  // Leaked JSON tool argument blocks (handles nested objects)
  cleaned = cleaned.replace(
    /\{\s*"(?:entity_name|tool_name|tool_code|context|temperature|scene_context|parameters|skill|dc_modifier|reason|item_name|target|action|quantity|category|weight|name)"[\s\S]*?\n\s*\}/g,
    ""
  );
  // Code fences (```json ... ``` or ``` ... ```) containing any content
  cleaned = cleaned.replace(/```[\s\S]*?```/gi, "");
  // Gemini-style <tool_code>...</tool_code> blocks (incl. unclosed)
  cleaned = cleaned.replace(/<tool_code>[\s\S]*?(?:<\/tool_code>|$)/gi, "");
  cleaned = cleaned.replace(/<\/?tool_code>/gi, "");
  // Orphaned print(...) calls leaked from tool_code
  cleaned = cleaned.replace(/(?:^|\n)\s*print\s*\([\s\S]*?\)\s*(?=\n|$)/gi, "\n");
  // Planning prose: strip any "I will use / I'll use / Let me use ..." sentence
  // wherever it appears. Each sentence consumes through its terminator.
  // Run repeatedly to catch back-to-back planning sentences.
  for (let i = 0; i < 5; i++) {
    const before = cleaned;
    cleaned = cleaned.replace(
      /\b(?:I will use|I'll use|I am going to use|I'm going to use|Let me use|I will call|I'll call)\b[^.!?\n]*[.!?]?\s*/gi,
      ""
    );
    cleaned = cleaned.replace(
      /\b(?:After the skill check|After the roll|Once the skill check resolves|When the roll resolves)\b[^.!?\n]*[.!?]?\s*/gi,
      ""
    );
    cleaned = cleaned.replace(
      /\b(?:I will narrate|I will then provide|I will provide|I will describe|I will emphasize|I will make sure|I will focus|I will activate|I will establish|I will update)\b[^.!?\n]*[.!?]?\s*/gi,
      ""
    );
    if (cleaned === before) break;
  }
  // CHARACTER AGENT markers: 🎭 CHARACTER AGENT (Name, temp=X):
  cleaned = cleaned.replace(/🎭\s*CHARACTER\s*AGENT\s*\([^)]*\)\s*:?\s*/gi, "");
  // [NARRATOR ONLY - ...] internal thought blocks
  cleaned = cleaned.replace(/\[NARRATOR\s*ONLY\s*-\s*[^\]]*\]/gi, "");
  // Stray 🎭 emoji
  cleaned = cleaned.replace(/🎭\s*/g, "");
  // Thinking/planning blocks
  cleaned = cleaned.replace(/\[THINKING\][\s\S]*?\[\/THINKING\]/gi, "");
  cleaned = cleaned.replace(/<thinking>[\s\S]*?<\/thinking>/gi, "");
  cleaned = cleaned.replace(/\[PLANNING\][\s\S]*?\[\/PLANNING\]/gi, "");
  // XML-style tool/function call wrappers
  cleaned = cleaned.replace(
    /<(?:function_call|tool_use|tool_call)[^>]*>[\s\S]*?<\/(?:function_call|tool_use|tool_call)>/gi,
    ""
  );
  cleaned = stripLeadingNarratorMeta(cleaned);
  cleaned = stripInlineTranslations(cleaned);
  cleaned = cleaned.replace(/\n{3,}/g, "\n\n").trim();
  if (!cleaned) return text.trim() ? text : "";
  return cleaned || text;
}

export function stripInlineTranslations(text: string): string {
  if (!text) return text;
  let cleaned = text;
  cleaned = cleaned.replace(/\s*\*\([^)]{3,}\)\*/g, "");
  cleaned = cleaned.replace(/\s*[\(\[]\s*(?:Translation|Meaning|Literally|In English|English|Which means|That means|Translated)[:\s—–-][^\)\]]{3,}[\)\]]/gi, "");
  cleaned = cleaned.replace(/\s*[—–-]\s*[""\u201C]([^""\u201D]{5,})[""\u201D]\s*(?=\n|$)/g, (match, inner) => {
    const latinRatio = (inner.match(/[a-zA-Z]/g) || []).length / inner.length;
    return latinRatio > 0.7 ? "" : match;
  });
  return cleaned.trim() ? cleaned : "";
}

/**
 * Strip markdown formatting for TTS — produces clean readable prose
 * with no bold, italic, headers, code, links, or internal tags.
 */
export function stripMarkdownForTTS(text: string): string {
  if (!text) return text;
  let clean = text;
  // Remove [INNER_VOICE] blocks (not spoken aloud)
  clean = clean.replace(/\[INNER_VOICE\][\s\S]*?\[\/INNER_VOICE\]/gi, "");
  // Run the full tool sanitizer first
  clean = sanitizeLeakedToolCalls(clean);
  // Bold markdown: **text** → text
  clean = clean.replace(/\*\*([^*]+)\*\*/g, "$1");
  // Italic markdown: *text* → text
  clean = clean.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "$1");
  // _italic_
  clean = clean.replace(/(?<!\w)_([^_]+)_(?!\w)/g, "$1");
  // Headers
  clean = clean.replace(/^#{1,6}\s+/gm, "");
  // Code blocks
  clean = clean.replace(/```[\s\S]*?```/g, "");
  clean = clean.replace(/`([^`]+)`/g, "$1");
  // Links: [text](url) → text
  clean = clean.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");
  // Image tags
  clean = clean.replace(/!\[([^\]]*)\]\([^)]+\)/g, "");
  // HTML tags
  clean = clean.replace(/<[^>]+>/g, "");
  // Leaked JSON blocks (additional safety net)
  clean = clean.replace(/\{\s*"(?:entity_name|tool_name|context|temperature)"[\s\S]*?\}/g, "");
  // Collapse whitespace
  clean = clean.replace(/\n{3,}/g, "\n\n").trim();
  return clean || text;
}
